<!-- eslint-disable vue/multi-word-component-names -->

<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div>
              <div>
                <div class="font-weight-bold text-body-2 lowercase">
                  {{ userDetails?.name || 'Carregando...' }}
                </div>
              </div>
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="bg-grey-lighten-5 main-content" style="margin-top: 20px">
      <!-- Content da Tela de Base de Conhecimento -->
      <v-container fluid class="pa-6">
        <!-- Header da Página -->
        <div class="page-header mb-6">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon size="32" :color="colors.primaryLight" class="mr-3 pulse">
                fas fa-brain
              </v-icon>
              <div>
                <h1 class="text-h4 font-weight-bold gradient-text mb-1">
                  Base de Conhecimento I.A
                </h1>
                <p class="text-subtitle-1 text-grey-darken-1">
                  Gerencie a base de conhecimentos que a IA utilizará nas conversas
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs para FAQs e Knowledge Base -->
        <v-tabs
          v-model="activeTab"
          :color="colors.primaryLight"
          class="mb-6"
        >
          <v-tab value="faqs" class="tab-item">
            <v-icon class="mr-2">fas fa-file-alt</v-icon>
            DOCUMENTOS EXTERNOS ({{ faqs.length }})
          </v-tab>
          <v-tab value="knowledge" class="tab-item">
            <v-icon class="mr-2">fas fa-database</v-icon>
            BASE INTERNA ({{ knowledgeBase.length }})
          </v-tab>
        </v-tabs>

        <!-- Tab Window -->
        <v-window v-model="activeTab">
          <!-- FAQs Tab -->
          <v-window-item value="faqs">
            <v-alert
              type="info"
              variant="tonal"
              :color="colors.primaryLight"
              class="mb-4"
              density="compact"
            >
              <div class="d-flex align-center">
                <v-icon size="20" class="mr-2">fas fa-info-circle</v-icon>
                <div>
                  <strong>Documentos Externos:</strong> Esta seção é para gerenciar documentos com arquivos anexados. 
                  Cada documento <strong>deve ter um arquivo</strong> (.pdf, .doc, .docx, .txt) para ser enviado à API.
                </div>
              </div>
            </v-alert>

            <div class="d-flex justify-space-between align-center mb-4">
              <h2 class="text-h6 font-weight-bold">Documentos Externos</h2>
              <v-btn
                :color="colors.primaryGreen"
                variant="elevated"
                prepend-icon="fas fa-plus"
                @click="addFaq"
              >
                Novo Documento
              </v-btn>
            </div>

            <v-row>
              <v-col
                v-for="(faq, index) in faqs"
                :key="`faq-${index}`"
                cols="12"
                md="6"
              >
                <v-card class="knowledge-card" :color="colors.lightBg" elevation="3">
                  <v-card-title class="card-title d-flex justify-space-between align-center">
                    <div class="d-flex align-center">
                      <v-icon :color="colors.primaryDark" class="mr-2">fas fa-file-alt</v-icon>
                      <span class="text-truncate">DOCUMENTO #{{ index + 1 }}</span>
                    </div>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="removeFaq(index)"
                      class="remove-btn"
                    >
                      <v-icon size="18" color="error">fas fa-trash</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="faq.question"
                      label="Título/Pergunta (Opcional)"
                      variant="outlined"
                      :color="colors.primaryLight"
                      class="mb-3"
                      density="compact"
                      hint="Apenas para referência, não é enviado para API"
                      persistent-hint
                    ></v-text-field>
                    
                    <v-textarea
                      v-model="faq.answer"
                      label="Descrição/Resposta (Opcional)"
                      variant="outlined"
                      rows="3"
                      :color="colors.primaryLight"
                      class="mb-3"
                      hint="Apenas para referência, não é enviado para API"
                      persistent-hint
                    ></v-textarea>

                    <!-- Upload de Arquivo -->
                    <div class="mb-4">
                      <v-file-input
                        :model-value="faq.file_name ? [{ name: faq.file_name }] : []"
                        label="Arquivo do Documento *"
                        variant="outlined"
                        :color="colors.primaryLight"
                        prepend-icon="fas fa-file"
                        accept=".pdf,.doc,.docx,.txt"
                        density="compact"
                        hint="Obrigatório para enviar documento"
                        persistent-hint
                        @change="(e) => handleFileUpload(e, index)"
                        @click:clear="removeFile(index)"
                      ></v-file-input>
                      <div v-if="faq.file_name" class="mt-2 d-flex align-center">
                        <v-icon size="16" color="success" class="mr-2">fas fa-check-circle</v-icon>
                        <span class="text-caption text-success font-weight-bold">{{ faq.file_name }}</span>
                      </div>
                    </div>

                    <v-row>
                      <v-col cols="6">
                        <v-text-field
                          v-model="faq.condominium_id"
                          label="ID Condomínio"
                          variant="outlined"
                          :color="colors.primaryLight"
                          density="compact"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="faq.condominium"
                          label="Nome Condomínio"
                          variant="outlined"
                          :color="colors.primaryLight"
                          density="compact"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-text-field
                      v-model="faq.category"
                      label="Categoria"
                      variant="outlined"
                      :color="colors.primaryLight"
                      density="compact"
                      class="mb-3"
                    ></v-text-field>

                    <v-combobox
                      v-model="faq.tags"
                      label="Tags"
                      variant="outlined"
                      :color="colors.primaryLight"
                      multiple
                      chips
                      density="compact"
                    ></v-combobox>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Empty State -->
            <v-alert
              v-if="faqs.length === 0"
              type="info"
              variant="tonal"
              :color="colors.primaryLight"
              class="mt-4"
            >
              <div class="text-center py-4">
                <v-icon size="48" :color="colors.primaryLight" class="mb-3">fas fa-folder-open</v-icon>
                <h3 class="text-h6 mb-2">Nenhum documento cadastrado</h3>
                <p class="text-body-2 mb-3">Comece adicionando um novo documento ou importe dados de um arquivo JSON.</p>
                <div class="d-flex justify-center gap-3">
                  <v-btn
                    :color="colors.primaryGreen"
                    variant="elevated"
                    prepend-icon="fas fa-plus"
                    @click="addFaq"
                  >
                    Adicionar Documento
                  </v-btn>
                  <v-btn
                    :color="colors.primaryDark"
                    variant="outlined"
                    prepend-icon="fas fa-file-import"
                    @click="importFromJson"
                  >
                    Importar JSON
                  </v-btn>
                </div>
              </div>
            </v-alert>
          </v-window-item>

          <!-- Knowledge Base Tab -->
          <v-window-item value="knowledge">
            <v-alert
              type="info"
              variant="tonal"
              :color="colors.primaryLight"
              class="mb-4"
              density="compact"
            >
              <div class="d-flex align-center">
                <v-icon size="20" class="mr-2">fas fa-info-circle</v-icon>
                <div>
                  <strong>Base Interna:</strong> Esta seção é para conhecimentos textuais que serão usados pela IA. 
                  Não requer arquivos anexados.
                </div>
              </div>
            </v-alert>

            <div class="d-flex justify-space-between align-center mb-4">
              <h2 class="text-h6 font-weight-bold">Base de Conhecimento</h2>
              <v-btn
                :color="colors.primaryGreen"
                variant="elevated"
                prepend-icon="fas fa-plus"
                @click="addKnowledge"
              >
                Novo Conhecimento
              </v-btn>
            </div>

            <v-row>
              <v-col
                v-for="(knowledge, index) in knowledgeBase"
                :key="`knowledge-${index}`"
                cols="12"
                lg="6"
              >
                <v-card class="knowledge-card" :color="colors.lightBg" elevation="3">
                  <v-card-title class="card-title d-flex justify-space-between align-center">
                    <div class="d-flex align-center">
                      <v-icon :color="colors.primaryDark" class="mr-2">fas fa-lightbulb</v-icon>
                      <span class="text-truncate">{{ knowledge.title || `Item #${index + 1}` }}</span>
                    </div>
                    <div class="d-flex align-center gap-2">
                      <v-chip
                        size="small"
                        :color="getPriorityColor(knowledge.priority)"
                        variant="elevated"
                      >
                        {{ knowledge.priority || 'medium' }}
                      </v-chip>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click="removeKnowledge(index)"
                        class="remove-btn"
                      >
                        <v-icon size="18" color="error">fas fa-trash</v-icon>
                      </v-btn>
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="knowledge.title"
                      label="Título"
                      variant="outlined"
                      :color="colors.primaryLight"
                      class="mb-3"
                      density="compact"
                    ></v-text-field>
                    
                    <v-textarea
                      v-model="knowledge.content"
                      label="Conteúdo"
                      variant="outlined"
                      rows="4"
                      :color="colors.primaryLight"
                      class="mb-3"
                    ></v-textarea>

                    <v-textarea
                      v-model="knowledge.context"
                      label="Contexto"
                      variant="outlined"
                      rows="2"
                      :color="colors.primaryLight"
                      class="mb-3"
                    ></v-textarea>

                    <v-row>
                      <v-col cols="6">
                        <v-text-field
                          v-model="knowledge.department"
                          label="Departamento"
                          variant="outlined"
                          :color="colors.primaryLight"
                          density="compact"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="knowledge.category"
                          label="Categoria"
                          variant="outlined"
                          :color="colors.primaryLight"
                          density="compact"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="6">
                        <v-select
                          v-model="knowledge.priority"
                          label="Prioridade"
                          :items="priorityOptions"
                          variant="outlined"
                          :color="colors.primaryLight"
                          density="compact"
                        ></v-select>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="knowledge.created_by"
                          label="Criado por"
                          variant="outlined"
                          :color="colors.primaryLight"
                          density="compact"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-combobox
                      v-model="knowledge.tags"
                      label="Tags"
                      variant="outlined"
                      :color="colors.primaryLight"
                      multiple
                      chips
                      density="compact"
                      class="mb-3"
                    ></v-combobox>

                    <div class="text-caption text-grey-darken-1">
                      <v-icon size="12" class="mr-1">fas fa-clock</v-icon>
                      Última atualização: {{ formatDate(knowledge.last_updated) }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Empty State -->
            <v-alert
              v-if="knowledgeBase.length === 0"
              type="info"
              variant="tonal"
              :color="colors.primaryLight"
              class="mt-4"
            >
              <div class="text-center py-4">
                <v-icon size="48" :color="colors.primaryLight" class="mb-3">fas fa-brain</v-icon>
                <h3 class="text-h6 mb-2">Nenhum conhecimento cadastrado</h3>
                <p class="text-body-2 mb-3">Comece adicionando um novo item de conhecimento ou importe dados de um arquivo JSON.</p>
                <div class="d-flex justify-center gap-3">
                  <v-btn
                    :color="colors.primaryGreen"
                    variant="elevated"
                    prepend-icon="fas fa-plus"
                    @click="addKnowledge"
                  >
                    Adicionar Conhecimento
                  </v-btn>
                  <v-btn
                    :color="colors.primaryDark"
                    variant="outlined"
                    prepend-icon="fas fa-file-import"
                    @click="importFromJson"
                  >
                    Importar JSON
                  </v-btn>
                </div>
              </div>
            </v-alert>
          </v-window-item>
        </v-window>

        <!-- Status de Upload -->
        <v-card 
          v-if="uploadedFiles.length > 0" 
          class="mt-4" 
          :color="colors.lightBg"
          elevation="2"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon :color="colors.primaryLight" class="mr-2">fas fa-paperclip</v-icon>
            Arquivos Anexados ({{ uploadedFiles.length }})
          </v-card-title>
          <v-card-text>
            <v-chip
              v-for="(file, idx) in uploadedFiles"
              :key="idx"
              class="ma-1"
              :color="colors.primaryGreen"
              variant="outlined"
            >
              <v-icon size="14" class="mr-1">fas fa-file</v-icon>
              {{ file.name }}
            </v-chip>
          </v-card-text>
        </v-card>

        <!-- Botões de Ação -->
        <div class="d-flex justify-center gap-4 mt-8">
          <v-btn
            :color="colors.primaryDark"
            size="large"
            variant="outlined"
            prepend-icon="fas fa-file-import"
            @click="importFromJson"
            class="action-btn"
          >
            Importar JSON
          </v-btn>
          <v-btn
            :color="colors.primaryGreen"
            size="large"
            variant="outlined"
            prepend-icon="fas fa-file-export"
            @click="exportToJson"
            class="action-btn"
          >
            Exportar JSON
          </v-btn>
          <v-btn
            :color="colors.primaryLight"
            size="large"
            variant="elevated"
            prepend-icon="fas fa-cloud-upload-alt"
            @click="saveKnowledgeBase"
            :loading="saving"
            class="action-btn"
          >
            Enviar para API
          </v-btn>
        </div>

        <!-- Dialog de Confirmação -->
        <v-dialog v-model="confirmDialog" max-width="400">
          <v-card>
            <v-card-title class="text-h6">Confirmar Ação</v-card-title>
            <v-card-text>{{ confirmMessage }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="confirmDialog = false">Cancelar</v-btn>
              <v-btn color="primary" @click="confirmAction">Confirmar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Input file oculto para importar -->
        <input
          ref="fileInput"
          type="file"
          accept=".json"
          style="display: none"
          @change="handleFileImport"
        >

        <!-- Snackbar de Feedback -->
        <v-snackbar
          v-model="snackbar"
          :color="snackbarColor"
          timeout="4000"
          top
        >
          {{ snackbarText }}
          <template v-slot:actions>
            <v-btn color="white" variant="text" @click="snackbar = false">
              Fechar
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits, computed } from "vue";
import { useRouter } from 'vue-router';
import axios from "axios";
import { useAuthStore } from '@/stores/auth'
import { menuItems } from '@/constants/MenuItems'
import { usePermissions } from '@/composables/usePermissions';

const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');

const props = defineProps({
  title: {
    type: String,
    default: 'SICON PAINEL DE CONTROLE I.A'
  },
  showUserProfile: {
    type: Boolean,
    default: true
  },
  customMenuItems: {
    type: Array,
    default: () => []
  }
});

const { 
  userDetails, 
  checkPermissionAndRedirect
} = usePermissions();
const isMounted = ref(false);
const checkAccessAndInitialize = async () => {
  const hasAccess = await checkPermissionAndRedirect('config');
  if (!hasAccess) {
    return false;
  }
  
  isMounted.value = true;
  await fetchUserDetails();
  return true;
};

const emit = defineEmits(['menu-item-click', 'user-loaded']);

// Composables
const router = useRouter();
const authStore = useAuthStore();
const auth = useAuthStore();
const token = auth?.token || null;

// State do Layout Original
const drawer = ref(true);

// State da Base de Conhecimento
const activeTab = ref('faqs');
const saving = ref(false);
const snackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');
const confirmDialog = ref(false);
const confirmMessage = ref('');
const confirmCallback = ref(null);
const fileInput = ref(null);
const uploadedFiles = ref([]);

// Dados
const faqs = ref([]);
const knowledgeBase = ref([]);

// Opções
const priorityOptions = [
  { title: 'Baixa', value: 'low' },
  { title: 'Média', value: 'medium' },
  { title: 'Alta', value: 'high' },
  { title: 'Crítica', value: 'critical' }
];

// eslint-disable-next-line no-unused-vars
const finalMenuItems = computed(() => {
  return props.customMenuItems.length > 0 ? props.customMenuItems : menuItems;
});

// eslint-disable-next-line no-unused-vars
const handleMenuClick = (item) => {
  emit('menu-item-click', item);
  if (item.route && router) {
    router.push(item.route);
  }
};

const fetchUserDetails = async () => {
  if (!token) return;

  try {
    const response = await axios.get(`${API_BASE}/me`, { 
      headers: { Authorization: `Bearer ${token}` } 
    });

    if (response.data) {
      userDetails.value = response.data;
      emit('user-loaded', response.data);
    }
  } catch (error) {
    console.error('Erro ao buscar dados do usuário:', error);
    userDetails.value = null;
  }
};

const logout = () => {
  if (authStore && typeof authStore.logout === 'function') {
    authStore.logout();
  }
  if (router && typeof router.push === 'function') {
    router.push('/');
  }
};

// Methods da Base de Conhecimento
const addFaq = () => {
  faqs.value.push({
    question: '',
    answer: '',
    condominium_id: 'all',
    condominium: 'Todos',
    category: '',
    tags: [],
    file_name: ''
  });
};

const handleFileUpload = (event, index) => {
  const file = event.target.files[0];
  if (!file) return;
  
  // Adicionar arquivo à lista de arquivos
  uploadedFiles.value.push(file);
  
  // Atualizar o file_name na FAQ
  faqs.value[index].file_name = file.name;
  
  showSnackbar(`Arquivo "${file.name}" adicionado com sucesso!`, 'success');
};

const removeFile = (index) => {
  const fileName = faqs.value[index].file_name;
  
  // Remover arquivo da lista
  const fileIndex = uploadedFiles.value.findIndex(f => f.name === fileName);
  if (fileIndex !== -1) {
    uploadedFiles.value.splice(fileIndex, 1);
  }
  
  // Limpar file_name
  faqs.value[index].file_name = '';
  
  showSnackbar('Arquivo removido', 'info');
};

const removeFaq = (index) => {
  confirmMessage.value = 'Tem certeza que deseja remover este documento?';
  confirmCallback.value = () => {
    // Remover arquivo associado se houver
    if (faqs.value[index].file_name) {
      removeFile(index);
    }
    faqs.value.splice(index, 1);
    showSnackbar('Documento removido com sucesso!', 'success');
  };
  confirmDialog.value = true;
};

const addKnowledge = () => {
  const now = new Date().toISOString();
  knowledgeBase.value.push({
    title: '',
    content: '',
    context: '',
    department: 'all',
    category: '',
    tags: [],
    priority: 'medium',
    last_updated: now,
    created_by: userDetails.value?.name || 'Sistema'
  });
};

const removeKnowledge = (index) => {
  confirmMessage.value = 'Tem certeza que deseja remover este item da base de conhecimento?';
  confirmCallback.value = () => {
    knowledgeBase.value.splice(index, 1);
    showSnackbar('Item removido com sucesso!', 'success');
  };
  confirmDialog.value = true;
};

const confirmAction = () => {
  if (confirmCallback.value) {
    confirmCallback.value();
  }
  confirmDialog.value = false;
  confirmCallback.value = null;
};

const getPriorityColor = (priority) => {
  const colors = {
    low: 'green',
    medium: 'orange',
    high: 'red',
    critical: 'purple'
  };
  return colors[priority] || 'grey';
};

const formatDate = (dateString) => {
  if (!dateString) return 'Não definido';
  try {
    return new Date(dateString).toLocaleString('pt-BR');
  } catch {
    return 'Data inválida';
  }
};

const showSnackbar = (message, color = 'success') => {
  snackbarText.value = message;
  snackbarColor.value = color;
  snackbar.value = true;
};

const saveKnowledgeBase = async () => {
  if (faqs.value.length === 0 && knowledgeBase.value.length === 0 && uploadedFiles.value.length === 0) {
    showSnackbar('Adicione pelo menos um item antes de salvar!', 'warning');
    return;
  }

  // Validar que documentos tenham arquivos
  const documentsWithoutFiles = faqs.value.filter(faq => !faq.file_name);
  if (documentsWithoutFiles.length > 0) {
    showSnackbar('Todos os documentos devem ter um arquivo anexado!', 'warning');
    return;
  }

  saving.value = true;
  
  try {
    // Atualizar timestamps dos knowledge base items
    const updatedKnowledgeBase = knowledgeBase.value.map(item => ({
      ...item,
      last_updated: new Date().toISOString()
    }));

    // Preparar payload conforme schema do Python
    const payload = {};
    
    // Mapear FAQs para DocumentRequest
    if (faqs.value.length > 0) {
      payload.documents = faqs.value.map(faq => ({
        condominium_id: faq.condominium_id || 'all',
        condominium: faq.condominium || 'Todos',
        category: faq.category || 'Geral',
        tags: Array.isArray(faq.tags) ? faq.tags : [],
        file_name: faq.file_name
      }));
    }
    
    // Adicionar knowledge_base
    if (knowledgeBase.value.length > 0) {
      payload.knowledge_base = updatedKnowledgeBase;
    }

    // Criar FormData para envio
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));

    // Adicionar arquivos
    uploadedFiles.value.forEach(file => {
      formData.append('files', file);
    });

    const response = await axios.post(
      `${API_BASE}/back-ia/knowledge-base`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (response.data) {
      const { message, knowledge_base_updated, documents_updated, total_updated } = response.data;
      
      let successMessage = message || 'Base de conhecimento salva com sucesso!';
      if (knowledge_base_updated !== undefined || documents_updated !== undefined) {
        successMessage += ` (${total_updated} itens atualizados)`;
      }
      
      showSnackbar(successMessage, 'success');
      
      // Atualizar os dados locais
      if (payload.knowledge_base) {
        knowledgeBase.value = updatedKnowledgeBase;
      }
      
      // Limpar arquivos após envio bem-sucedido
      uploadedFiles.value = [];
    }
  } catch (error) {
    console.error('Erro ao salvar base de conhecimento:', error);
    
    const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.response?.data?.error ||
                        'Erro ao salvar base de conhecimento';
    
    showSnackbar(`Erro: ${errorMessage}`, 'error');
  } finally {
    saving.value = false;
  }
};

// eslint-disable-next-line no-unused-vars
const loadKnowledgeBase = async () => {
  showSnackbar('Esta funcionalidade não está disponível. Use "Importar JSON" para carregar dados existentes.', 'info');
};

const exportToJson = () => {
  const data = {
    documents: faqs.value.map(faq => ({
      condominium_id: faq.condominium_id || 'all',
      condominium: faq.condominium || 'Todos',
      category: faq.category || '',
      tags: Array.isArray(faq.tags) ? faq.tags : [],
      file_name: faq.file_name || '',
      // Campos extras para referência (não enviados para API)
      _reference: {
        question: faq.question,
        answer: faq.answer
      }
    })),
    knowledge_base: knowledgeBase.value,
    exported_at: new Date().toISOString(),
    exported_by: userDetails.value?.name || 'Sistema'
  };
  
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `knowledge-base-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
  
  showSnackbar('Dados exportados com sucesso!', 'success');
};

const importFromJson = () => {
  fileInput.value?.click();
};

const handleFileImport = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);
      
      // Importar documents/FAQs
      if (data.documents && Array.isArray(data.documents)) {
        faqs.value = data.documents.map(doc => ({
          question: doc._reference?.question || '',
          answer: doc._reference?.answer || '',
          condominium_id: doc.condominium_id || 'all',
          condominium: doc.condominium || 'Todos',
          category: doc.category || '',
          tags: Array.isArray(doc.tags) ? doc.tags : [],
          file_name: doc.file_name || ''
        }));
      } else if (data.faqs && Array.isArray(data.faqs)) {
        // Suporte para formato antigo
        faqs.value = data.faqs.map(faq => ({
          ...faq,
          file_name: faq.file_name || ''
        }));
      }
      
      // Importar knowledge_base
      if (data.knowledge_base && Array.isArray(data.knowledge_base)) {
        knowledgeBase.value = data.knowledge_base;
      }
      
      showSnackbar('Dados importados com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao importar:', error);
      showSnackbar('Erro ao importar arquivo JSON', 'error');
    }
  };
  reader.readAsText(file);
  
  // Limpar o input
  event.target.value = '';
};

// Lifecycle
onMounted(async() => {
  if (props.showUserProfile) {
    fetchUserDetails();
  }
  await checkAccessAndInitialize();
});

// Colors
const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};
</script>

<style scoped>
/* Animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

/* Layout Original Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 4px 0;
  position: relative;
  overflow: hidden;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.lowercase {
  text-transform: lowercase;
}

/* Styles da Tela de Configuração IA */
.page-header {
  background: linear-gradient(135deg, rgba(87, 194, 227, 0.1), rgba(163, 201, 64, 0.1));
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(87, 194, 227, 0.2);
}

.knowledge-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  border: 1px solid rgba(18, 58, 93, 0.1);
  position: relative;
  overflow: hidden;
}

.knowledge-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(45deg, #57C2E3, #A3C940);
}

.knowledge-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 58, 93, 0.15);
}

.card-title {
  font-weight: 600;
  color: #123A5D;
  padding: 20px 20px 12px 20px;
  font-size: 1rem;
}

.save-btn {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1);
  box-shadow: 0 4px 15px rgba(87, 194, 227, 0.4);
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(87, 194, 227, 0.6);
}

.action-btn {
  min-width: 140px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover {
  transform: translateY(-1px);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .user-profile span:last-child {
    display: none;
  }
  
  .page-header .d-flex {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start !important;
  }
  
  .d-flex.gap-4 {
    flex-direction: column;
    width: 100%;
  }
  
  .action-btn {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }
  
  .card-title {
    font-size: 0.9rem;
  }
}
</style>
